import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  type APIResponse,
  sendVerificationResult,
  uploadBlobToDrive,
} from "../lib/api";
import { captureVideoFrame } from "../lib/capture";
import {
  ADULT_MIN_BUCKET_INDEX,
  ANGLE_PROXIMITY,
  COMPLETION_THRESHOLD,
  DESCRIPTOR_THRESHOLD,
  GOOGLENET_LABELS,
  MAX_FAILURE_ATTEMPTS,
  MIN_ANGLE_STEP,
  NO_DETECTION_TIMEOUT_MS,
  RING_SEGMENTS_COUNT,
  SMILE_THRESHOLD,
} from "../lib/constants";
import { anglesDistance, euclideanDistance } from "../lib/geometry";

import { useCamera } from "./useCamera";
import type { FaceDetectionResult } from "../lib/faceDetection.ts";
import {
  useFaceDetectionWorker,
  workerAllowed,
} from "./useFaceDetectionWorker";
import type { VerificationResult } from "../types/verification";

const STRAIGHT = Number.POSITIVE_INFINITY;

const DEBUG =
  typeof window !== "undefined" && window.location.href.includes("?debug");

const UPLOAD_ENABLED = import.meta.env.VITE_ENABLE_SCREENSHOT_UPLOAD === "true";

type DetectionWithAngle = FaceDetectionResult & {
  angle?: { pitch: number; yaw: number };
};

type DetectionWithExpressions = FaceDetectionResult & {
  expressions?: { happy: number };
};

type DetectionWithAngleAndExpressions = DetectionWithAngle &
  DetectionWithExpressions;

type PlainSmileDetection = {
  bucketIndex: number;
  smile: number;
  descriptor: Float32Array;
};

type PlainFaceDetection = { angle: number } & PlainSmileDetection;

export type ChallengePhase = "move" | "smile" | "dont_smile";
export type Phase = ChallengePhase | "idle" | "loading" | "done" | "error";

export type IndicatorMode = "idle" | "loading" | "error" | "circled" | "active";

export interface IndicatorState {
  activeAngles: number[];
  mode: IndicatorMode;
  loadingAngle?: number;
  // Debug: current head angle from face-api (undefined when no detection or straight)
  currentAngle?: number;
}

function isMobile(): boolean {
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

function getAngleFromDetection(
  detection: DetectionWithAngle,
): number | undefined {
  const pitch = detection.angle?.pitch ?? 0;
  const yaw = detection.angle?.yaw ?? 0;
  const pitchN = 2.5 * pitch;
  if (Math.abs(pitchN) <= 2 && Math.abs(yaw) <= 2) {
    return undefined;
  }
  const angle = (Math.atan2(yaw, pitchN) / Math.PI) * 180;
  return ((angle % 360) + 360) % 360;
}

function getResultDelay(): number {
  return DEBUG ? 2000 : 300;
}

function isVideoReady(video: HTMLVideoElement): boolean {
  return (
    video.readyState >= 2 &&
    video.videoWidth > 0 &&
    video.videoHeight > 0 &&
    !video.paused &&
    !video.ended
  );
}

function isNewFaceAngle(
  angle: number,
  detections: PlainFaceDetection[],
): boolean {
  const existingAngles = detections
    .filter((d) => d.angle !== STRAIGHT)
    .map((d) => d.angle);
  return (
    existingAngles.length === 0 ||
    existingAngles.every((a) => anglesDistance(angle, a) >= MIN_ANGLE_STEP)
  );
}

function isNoActivity(lastActivityDate: number): boolean {
  return Date.now() - lastActivityDate > NO_DETECTION_TIMEOUT_MS;
}

function bucketIndexToResult(idx: number): VerificationResult {
  const clamped = Math.max(0, Math.min(idx, GOOGLENET_LABELS.length - 1));
  return {
    passed: clamped >= ADULT_MIN_BUCKET_INDEX,
    bucket: GOOGLENET_LABELS[clamped],
    bucketIndex: clamped,
  };
}

function setupResult(
  detections: Array<{ bucketIndex: number }>,
): VerificationResult {
  const avgIndex =
    detections.reduce((acc, d) => acc + d.bucketIndex, 0) / detections.length;
  return bucketIndexToResult(Math.round(avgIndex));
}

async function captureAndUpload(
  video: HTMLVideoElement,
  screenshots: string[],
  promises: Promise<void>[],
): Promise<void> {
  if (!UPLOAD_ENABLED) {
    return;
  }

  captureVideoFrame(video)
    .then((blob) => {
      if (!blob) {
        return;
      }
      const uploadingPromise = uploadBlobToDrive(blob).then((url) => {
        if (url) screenshots.push(url);
      });
      promises.push(uploadingPromise);
    })
    .catch((error) => console.error("Upload failed:", error));
}

function uploadOnFirstDetection(
  detections: PlainFaceDetection[],
  uploadsCounter: RefObject<number>,
  screenshots: string[],
  promises: Promise<void>[],
  video: HTMLVideoElement | null,
) {
  if (detections.length !== 0) {
    return;
  }
  if (uploadsCounter.current > 0) {
    return;
  }
  if (!video || !isVideoReady(video)) {
    return;
  }
  uploadsCounter.current += 1;
  captureAndUpload(video, screenshots, promises);
  return;
}

function uploadOnRandomDetection(
  detections: PlainFaceDetection[],
  uploadsCounter: RefObject<number>,
  screenshots: string[],
  promises: Promise<void>[],
  video: HTMLVideoElement | null,
) {
  if (detections.length === 0) {
    return;
  }
  if (uploadsCounter.current < 2 || uploadsCounter.current > 2) {
    return;
  }
  if (!video || !isVideoReady(video)) {
    return;
  }
  if (Math.random() < 0.8) {
    return;
  }
  uploadsCounter.current += 1;
  captureAndUpload(video, screenshots, promises);
  return;
}

function uploadOnFirstSmileDetection(
  detections: PlainSmileDetection[],
  uploadsCounter: RefObject<number>,
  screenshots: string[],
  promises: Promise<void>[],
  video: HTMLVideoElement | null,
) {
  if (detections.length > 0) {
    return;
  }
  if (uploadsCounter.current >= 3) {
    return;
  }
  if (!video || !isVideoReady(video)) {
    return;
  }
  uploadsCounter.current += 1;
  captureAndUpload(video, screenshots, promises);
  return;
}

function shutdownCamera(video: HTMLVideoElement | null) {
  if (!video || !(video.srcObject instanceof MediaStream)) {
    return;
  }
  video.srcObject.getTracks().forEach((t) => t.stop());
  video.srcObject = null;
}

export function useVerificationFlow(
  videoRef: React.RefObject<HTMLVideoElement | null>,
) {
  const { requestPermission } = useCamera();
  const { loadModels, detect, progress } = useFaceDetectionWorker();
  const useWorkerRef = useRef(workerAllowed);

  const [phase, setPhase] = useState<Phase>("idle");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [indicatorState, setIndicatorState] = useState<IndicatorState>({
    activeAngles: [],
    mode: "idle",
  });
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [faceClass, setFaceClass] = useState("");
  const [debugResult, setDebugResult] = useState<VerificationResult | null>(
    null,
  );
  const [serverResult, setServerResult] = useState<APIResponse | null>(null);

  const detectionsRef = useRef<Array<PlainFaceDetection>>([]);
  const smileDetectionsRef = useRef<Array<PlainSmileDetection>>([]);
  const lastDescriptorRef = useRef<Float32Array | null>(null);
  const lastDetectionRef = useRef(0);
  const challengeRef = useRef<"move" | "smile" | "dont_smile">("move");
  const resultRef = useRef<VerificationResult | null>(null);
  const loadingRef = useRef(true);
  const rafRef = useRef<number>(0);
  const mobileRef = useRef(isMobile());
  const phaseRef = useRef(phase);
  const failureCountRef = useRef(0);
  const screenshotUrlsRef = useRef<string[]>([]);
  const uploadsCountRef = useRef(0);
  const pendingUploadPromisesRef = useRef<Promise<void>[]>([]);

  phaseRef.current = phase;

  useEffect(() => {
    setLoadingProgress(progress);
  }, [progress]);

  const sendToBackend = useCallback(
    async (res: VerificationResult, screenshotUrls: string[] = []) => {
      try {
        const response = await sendVerificationResult(res, screenshotUrls);
        if (response.ok) {
          window.Telegram?.WebApp?.close();
          return;
        }
        shutdownCamera(videoRef.current);
        setServerResult(response);
      } catch (err) {
        console.error("Verification API error:", err);
      }
    },
    [],
  );

  const startVerification = useCallback(async () => {
    if (phase !== "idle" && phase !== "error") return;

    setPhase("loading");
    setIndicatorState({ activeAngles: [], mode: "loading", loadingAngle: 0 });
    setHint("loading");
    setError(null);
    setServerResult(null);
    loadingRef.current = true;

    const stream = await requestPermission();
    if (!stream || !videoRef.current) {
      setPhase("error");
      setError("camera_not_granted");
      setIndicatorState({ activeAngles: [], mode: "error" });
      setHint(null);
      return;
    }

    videoRef.current.srcObject = stream;
    try {
      await videoRef.current.play();
    } catch {
      setPhase("error");
      setError("camera_not_granted");
      setIndicatorState({ activeAngles: [], mode: "error" });
      setHint(null);
      return;
    }

    await loadModels();
    challengeRef.current = "move";
    detectionsRef.current = [];
    smileDetectionsRef.current = [];
    lastDescriptorRef.current = null;
    resultRef.current = null;
    uploadsCountRef.current = 0;
    screenshotUrlsRef.current = [];
    pendingUploadPromisesRef.current = [];

    function resetToMovePhase() {
      detectionsRef.current = [];
      smileDetectionsRef.current = [];
      lastDescriptorRef.current = null;
      resultRef.current = null;
      challengeRef.current = "move";
      setPhase("move");
      setHint("move");
      setIndicatorState({ activeAngles: [], mode: "idle" });
      setFaceClass("");
      rafRef.current = requestAnimationFrame(onFrame);
    }

    function resetToSmilePhase() {
      detectionsRef.current = [];
      smileDetectionsRef.current = [];
      lastDescriptorRef.current = null;
      challengeRef.current = "move";
      setHint("move");
      setFaceClass("");
    }

    function scheduleDoneAndSend(res: VerificationResult, delayMs: number) {
      setTimeout(async () => {
        await Promise.allSettled(pendingUploadPromisesRef.current);
        setPhase("done");
        sendToBackend(res, [...screenshotUrlsRef.current]);
      }, delayMs);
    }

    function processMovePhase(
      detection: DetectionWithAngleAndExpressions | null,
    ) {
      const detections = detectionsRef.current;
      const video = videoRef.current;
      const screenshots = screenshotUrlsRef.current;
      const uploadPromises = pendingUploadPromisesRef.current;

      if (detection) {
        uploadOnFirstDetection(
          detections,
          uploadsCountRef,
          screenshots,
          uploadPromises,
          video,
        );
        uploadOnRandomDetection(
          detections,
          uploadsCountRef,
          screenshots,
          uploadPromises,
          video,
        );

        const smile = detection.expressions?.happy ?? 0;
        const angle = getAngleFromDetection(detection);
        lastDetectionRef.current = Date.now();

        if (angle === undefined) {
          detections.push({ ...detection, angle: STRAIGHT, smile });
        } else if (isNewFaceAngle(angle, detections)) {
          detections.push({ ...detection, angle, smile });
        }

        if (DEBUG) setDebugResult(bucketIndexToResult(detection.bucketIndex));
      } else if (isNoActivity(lastDetectionRef.current)) {
        detectionsRef.current = [];
      }

      const activeAngles: number[] = [];
      for (let i = 0; i < RING_SEGMENTS_COUNT; i++) {
        const a = (i / RING_SEGMENTS_COUNT) * 360;
        const hasMatch = detections.some(
          (d) =>
            d.angle !== STRAIGHT &&
            anglesDistance(d.angle, a) <= ANGLE_PROXIMITY,
        );
        if (hasMatch) activeAngles.push(a);
      }
      const done = activeAngles.length / RING_SEGMENTS_COUNT;

      if (done > COMPLETION_THRESHOLD) {
        const wasSmiling =
          detections.reduce((acc, d) => acc + d.smile, 0) /
            Math.max(1, detections.length) >
          SMILE_THRESHOLD;
        const res = setupResult(detections);
        resultRef.current = res;
        if (DEBUG) setDebugResult(res);
        setHint(null);

        if (!res.passed) {
          setIndicatorState({ activeAngles: [], mode: "error" });
          failureCountRef.current += 1;
          const delay = getResultDelay();
          if (failureCountRef.current >= MAX_FAILURE_ATTEMPTS) {
            scheduleDoneAndSend(res, delay);
            return;
          }
          setTimeout(() => resetToMovePhase(), delay);
          return;
        }

        setIndicatorState({ activeAngles: [], mode: "circled" });
        challengeRef.current = wasSmiling ? "dont_smile" : "smile";
        setFaceClass("circled");
        setTimeout(() => {
          setHint(wasSmiling ? "dont_smile" : "smile");
          rafRef.current = requestAnimationFrame(onFrame);
        }, 100);
        return;
      }
      setIndicatorState({
        activeAngles,
        mode: "idle",
        currentAngle:
          DEBUG && detection ? getAngleFromDetection(detection) : undefined,
      });
      rafRef.current = requestAnimationFrame(onFrame);
    }

    function processSmilePhase(detection: FaceDetectionResult | null) {
      const smileDetections = smileDetectionsRef.current;
      const challenge = challengeRef.current;
      const video = videoRef.current;
      const screenshots = screenshotUrlsRef.current;
      const uploadPromises = pendingUploadPromisesRef.current;

      if (!detection) {
        if (isNoActivity(lastDetectionRef.current)) {
          resetToSmilePhase();
        }
        rafRef.current = requestAnimationFrame(onFrame);
        return;
      }

      uploadOnFirstSmileDetection(
        smileDetections,
        uploadsCountRef,
        screenshots,
        uploadPromises,
        video,
      );

      const smile = detection.expressions?.happy ?? 0;
      smileDetections.push({ ...detection, smile });

      lastDetectionRef.current = Date.now();
      const isSmiling = smile > SMILE_THRESHOLD;
      const needSmiling = challenge === "smile";
      const passed = isSmiling === needSmiling;

      if (!passed) {
        rafRef.current = requestAnimationFrame(onFrame);
        return;
      }

      const smileRes = setupResult(smileDetections);
      const prev = resultRef.current;
      const finalIndex = prev
        ? Math.round(prev.bucketIndex * 0.8 + smileRes.bucketIndex * 0.2)
        : smileRes.bucketIndex;
      const res = bucketIndexToResult(finalIndex);
      resultRef.current = res;
      if (DEBUG) setDebugResult(res);
      setFaceClass("circled active");
      setHint(null);
      setIndicatorState({ activeAngles: [], mode: "active" });

      const delay = getResultDelay();
      if (res.passed) {
        scheduleDoneAndSend(res, delay);
        return;
      }
      failureCountRef.current += 1;
      if (failureCountRef.current >= MAX_FAILURE_ATTEMPTS) {
        scheduleDoneAndSend(res, delay);
        return;
      }
      setTimeout(() => resetToMovePhase(), delay);
    }

    const onFrame = async () => {
      const video = videoRef.current;
      if (!video) return;
      if (!isVideoReady(video)) {
        rafRef.current = requestAnimationFrame(onFrame);
        return;
      }

      let detection: FaceDetectionResult | null = null;
      try {
        if (useWorkerRef.current) {
          const bitmap = await createImageBitmap(video);
          detection = await detect(bitmap, mobileRef.current);
          bitmap.close();
        } else {
          detection = await detect(video, mobileRef.current);
        }
      } catch (err) {
        console.warn("Detection failed:", err);
      }

      if (loadingRef.current) {
        loadingRef.current = false;
        setPhase("move");
        setIndicatorState({ activeAngles: [], mode: "idle" });
        setHint("move");
      }

      const detections = detectionsRef.current;
      const smileDetections = smileDetectionsRef.current;
      const challenge = challengeRef.current;

      if (detection?.descriptor) {
        const allDescriptors = [
          ...detections.map((d) => d.descriptor),
          ...smileDetections.map((d) => d.descriptor),
        ];
        if (allDescriptors.length > 0) {
          const avgDistance =
            allDescriptors.reduce(
              (acc, d) => acc + euclideanDistance(d, detection!.descriptor),
              0,
            ) / allDescriptors.length;
          if (avgDistance >= DESCRIPTOR_THRESHOLD) {
            detectionsRef.current = [];
            smileDetectionsRef.current = [];
            lastDescriptorRef.current = null;
            challengeRef.current = "move";
            setFaceClass("");
            setHint("another_face");
            setIndicatorState({ activeAngles: [], mode: "error" });
            setTimeout(() => {
              setHint("move");
              rafRef.current = requestAnimationFrame(onFrame);
            }, 500);
            return;
          }
        }
      }

      if (challenge === "move") {
        processMovePhase(detection);
      } else if (challenge === "smile" || challenge === "dont_smile") {
        processSmilePhase(detection);
      } else {
        rafRef.current = requestAnimationFrame(onFrame);
      }
    };

    rafRef.current = requestAnimationFrame(onFrame);

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [phase, requestPermission, loadModels, detect, videoRef, sendToBackend]);

  const loadingProgressRef = useRef(loadingProgress);
  loadingProgressRef.current = loadingProgress;

  const loadingAngleRef = useRef(0);
  const loadingLastTimeRef = useRef(0);

  useEffect(() => {
    loadingAngleRef.current = 0;
    loadingLastTimeRef.current = Date.now();

    const animateLoading = () => {
      if (phaseRef.current !== "loading") return;
      const now = Date.now();
      loadingAngleRef.current =
        (loadingAngleRef.current +
          360 * ((now - loadingLastTimeRef.current) / 1000)) %
        360;
      loadingLastTimeRef.current = now;
      const swipe = 20 + loadingProgressRef.current * 150;
      const activeAngles: number[] = [];
      for (let i = 0; i < RING_SEGMENTS_COUNT; i++) {
        const a = (i / RING_SEGMENTS_COUNT) * 360;
        if (
          Math.min(
            Math.abs(a - loadingAngleRef.current),
            360 - Math.abs(a - loadingAngleRef.current),
          ) < swipe
        ) {
          activeAngles.push(a);
        }
      }
      setIndicatorState({
        activeAngles,
        mode: "loading",
        loadingAngle: loadingAngleRef.current,
      });
      requestAnimationFrame(animateLoading);
    };
    const id = requestAnimationFrame(animateLoading);
    return () => cancelAnimationFrame(id);
  }, [phase]);

  return {
    phase,
    indicatorState,
    hint,
    error,
    faceClass,
    debugResult,
    serverResult,
    startVerification,
  };
}
